import Empty from "antd/lib/empty";
import Flex from "antd/lib/flex";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { interpolate, Route } from "@sol/routing";
import { getResourceUuid, type IClassroomItem, type IMediaResource } from "@sol/sdk";
import { ArrowRight } from "@sol/uikit/core/icons/Arrows/ArrowRight";
import { PlaceholderPicture } from "@sol/uikit/data-display/PlaceholderPicture";
import { Skeleton } from "@sol/uikit/feedback/Skeleton/Skeleton";
import { Button } from "@sol/uikit/general/Button/Button";

/** Every classroom shape carrying what the list displays, whether it comes from a collection or a single resource. */
type ListedClassroom = Pick<IClassroomItem, "@id" | "title"> & { image?: IMediaResource | null };

type ClassroomListProps = {
    className?: string;
    classrooms?: ListedClassroom[];
    isLoading?: boolean;
    /** Replaces the default wording when the list holds nothing, to say why in this context. */
    emptyMessage?: string;
    /** Replaces the default action offered on each classroom. */
    renderClassroomAction?: (classroom: ListedClassroom) => ReactNode;
};

export function ClassroomsList({
    className,
    classrooms = [],
    isLoading,
    emptyMessage,
    renderClassroomAction,
}: ClassroomListProps) {
    const [t] = useTranslation();

    return (
        <Flex className={clsx("h-full overflow-auto", className)}>
            {isLoading ? (
                <Skeleton.Node className="size-full!" active />
            ) : classrooms.length > 0 ? (
                <ul className="flex size-full flex-col gap-ant-sm overflow-y-auto">
                    {classrooms.map(classroom => (
                        <li
                            key={classroom["@id"]}
                            className="flex items-center gap-ant rounded-ant border-ant border-ant-secondary bg-ant-fill-quaternary p-ant-xs pr-ant-lg max-sm:flex-col"
                        >
                            {classroom.image?.url ? (
                                <Image
                                    className="rounded-ant-sm"
                                    src={classroom.image.url}
                                    width={80}
                                    height={50}
                                    layout="intrinsic"
                                    objectFit="cover"
                                />
                            ) : (
                                <PlaceholderPicture className="block h-[50px] w-20 shrink-0 rounded-ant-sm" />
                            )}

                            <h2
                                title={classroom.title}
                                aria-label={classroom.title}
                                className="line-clamp-2 flex-1 shrink-0 overflow-hidden font-ant text-ant-lg leading-ant-lg font-semibold text-ellipsis max-sm:text-center"
                            >
                                {classroom.title}
                            </h2>
                            {renderClassroomAction?.(classroom) ?? (
                                <Link
                                    href={interpolate(Route.CLASSROOM_DASHBOARD, {
                                        classroomId: getResourceUuid(classroom),
                                    })}
                                    passHref
                                >
                                    <Button
                                        className="max-w-36 truncate"
                                        size="small"
                                        iconPosition="end"
                                        icon={<ArrowRight variant="antd" />}
                                        title={t("entities.classroom.components.ClassroomsList.item.cta.a11y", {
                                            classroomName: classroom.title,
                                        })}
                                        aria-label={t("entities.classroom.components.ClassroomsList.item.cta.a11y", {
                                            classroomName: classroom.title,
                                        })}
                                    >
                                        {t("entities.classroom.components.ClassroomsList.item.cta.title")}
                                    </Button>
                                </Link>
                            )}
                        </li>
                    ))}
                </ul>
            ) : (
                <Empty
                    // Auto margins centre the placeholder on both axes, whatever height the list is given
                    className="m-auto h-16"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={emptyMessage ?? t("entities.classroom.components.ClassroomsList.empty")}
                />
            )}
        </Flex>
    );
}
